import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from 'aws-amplify';
import {
  Typography,
  Button,
  Box,
  Container,
  Paper,
  TextField,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';

interface Question {
  id: string;
  content: string;
  is_html: boolean;
}

interface Interview {
  uuid: string;
  test_uuid: string;
  name: string;
  email: string;
  status: 'pending' | 'in_progress' | 'completed';
}

interface Test {
  uuid: string;
  name: string;
}

const InterviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<Test | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [interview, setInterview] = useState<Interview | null>(null);
  
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlobs, setRecordedBlobs] = useState<Blob[]>([]);
  
  const [confirmExitDialog, setConfirmExitDialog] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchTestData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch test details
        const testResponse = await API.get('interviewzen', `/tests/${id}`, {
          headers: {
            'Accept': 'application/json',
          },
          queryStringParameters: {
            public: true,
          },
        });
        
        setTest(testResponse);
        
        // Fetch questions
        const questionsResponse = await API.get('interviewzen', `/tests/${id}/questions`, {
          headers: {
            'Accept': 'application/json',
          },
          queryStringParameters: {
            public: true,
          },
        });
        
        setQuestions(questionsResponse);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching test data:', error);
        setError('Failed to load interview. The test may not exist or is no longer available.');
        setLoading(false);
      }
    };
    
    if (id) {
      fetchTestData();
    }
    
    // Cleanup function to stop media streams when component unmounts
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [id]);

  const startInterview = async () => {
    // Validate form
    let hasError = false;
    
    if (!name.trim()) {
      setNameError('Name is required');
      hasError = true;
    } else {
      setNameError('');
    }
    
    if (!email.trim()) {
      setEmailError('Email is required');
      hasError = true;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Please enter a valid email address');
      hasError = true;
    } else {
      setEmailError('');
    }
    
    if (hasError) return;
    
    try {
      // Create interview
      const interviewResponse = await API.post('interviewzen', `/tests/${id}/interviews`, {
        body: {
          name: name.trim(),
          email: email.trim(),
        },
      });
      
      setInterview(interviewResponse);
      
      // Start interview
      await API.put('interviewzen', `/interviews/${interviewResponse.uuid}/start`, {});
      
      // Move to questions
      setStep(1);
    } catch (error) {
      console.error('Error starting interview:', error);
      setError('Failed to start interview. Please try again.');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        setRecordedBlobs([...recordedBlobs, blob]);
        chunks.length = 0;
      };
      
      mediaRecorder.start();
      setRecording(true);
      
      // Start timer
      let seconds = 0;
      timerRef.current = setInterval(() => {
        seconds += 1;
        setRecordingTime(seconds);
      }, 1000);
    } catch (error) {
      console.error('Error accessing media devices:', error);
      setError('Failed to access camera and microphone. Please ensure you have granted the necessary permissions.');
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setRecording(false);
    
    // Upload recording if we have an interview
    if (interview && recordedBlobs.length > 0) {
      try {
        const currentQuestion = questions[activeQuestion];
        const lastBlob = recordedBlobs[recordedBlobs.length - 1];
        
        // Convert blob to base64
        const reader = new FileReader();
        reader.readAsDataURL(lastBlob);
        reader.onloadend = async () => {
          const base64data = reader.result as string;
          
          // Submit answer
          await API.post('interviewzen', `/interviews/${interview.uuid}/answers`, {
            body: {
              question_id: currentQuestion.id,
              recorder: 'browser',
              frames: base64data.split(',')[1], // Remove the data URL prefix
              duration: recordingTime,
            },
          });
        };
      } catch (error) {
        console.error('Error uploading recording:', error);
        setError('Failed to save your answer. Please try again.');
      }
    }
  };

  const handleNextQuestion = () => {
    if (activeQuestion < questions.length - 1) {
      setActiveQuestion(activeQuestion + 1);
    } else {
      // Complete interview
      completeInterview();
    }
  };

  const completeInterview = async () => {
    if (!interview) return;
    
    try {
      await API.put('interviewzen', `/interviews/${interview.uuid}/complete`, {});
      setStep(2);
    } catch (error) {
      console.error('Error completing interview:', error);
      setError('Failed to complete interview. Please try again.');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleExit = () => {
    if (step === 1 && !confirmExitDialog) {
      setConfirmExitDialog(true);
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {test?.name || 'Interview'}
        </Typography>
        
        <Stepper activeStep={step} sx={{ mb: 4 }}>
          <Step>
            <StepLabel>Start</StepLabel>
          </Step>
          <Step>
            <StepLabel>Questions</StepLabel>
          </Step>
          <Step>
            <StepLabel>Complete</StepLabel>
          </Step>
        </Stepper>
        
        {step === 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Welcome to the Interview
            </Typography>
            <Typography variant="body1" paragraph>
              Please enter your information to begin. You will be asked to record your answers to a series of questions.
            </Typography>
            
            <Box component="form" noValidate sx={{ mt: 2 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Your Name"
                name="name"
                autoComplete="name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!nameError}
                helperText={nameError}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError}
              />
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={handleExit}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={startInterview}
                >
                  Start Interview
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
        
        {step === 1 && questions.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Question {activeQuestion + 1} of {questions.length}
            </Typography>
            
            <Box sx={{ my: 3 }}>
              <Typography variant="body1">
                {questions[activeQuestion].content}
              </Typography>
            </Box>
            
            <Box sx={{ my: 3 }}>
              <Box sx={{ position: 'relative', width: '100%', height: 'auto', minHeight: '300px', bgcolor: 'black' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  style={{ width: '100%', height: '100%' }}
                />
                
                {recording && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2">
                      Recording: {formatTime(recordingTime)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleExit}>
                Exit Interview
              </Button>
              
              <Box>
                {!recording ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={startRecording}
                  >
                    Start Recording
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={stopRecording}
                  >
                    Stop Recording
                  </Button>
                )}
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNextQuestion}
                  disabled={recording || recordedBlobs.length === 0}
                  sx={{ ml: 2 }}
                >
                  {activeQuestion < questions.length - 1 ? 'Next Question' : 'Finish Interview'}
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
        
        {step === 2 && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Interview Completed
            </Typography>
            <Typography variant="body1" paragraph>
              Thank you for completing the interview. Your responses have been recorded.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/')}
              sx={{ mt: 2 }}
            >
              Return to Home
            </Button>
          </Paper>
        )}
      </Box>
      
      {/* Confirm Exit Dialog */}
      <Dialog
        open={confirmExitDialog}
        onClose={() => setConfirmExitDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Exit Interview?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to exit the interview? Your progress will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExitDialog(false)}>Cancel</Button>
          <Button onClick={() => navigate('/')} color="error" autoFocus>
            Exit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InterviewPage;
